import { useState, useMemo, useEffect } from 'react';
import { isBrowser } from '@phx-husky/browser-helpers';

var ConditionOperator;
(function (ConditionOperator) {
    ConditionOperator["GT"] = "Gt";
    ConditionOperator["LT"] = "Lt";
    ConditionOperator["GE"] = "Ge";
    ConditionOperator["LE"] = "Le";
    ConditionOperator["EQ"] = "Eq";
    ConditionOperator["NE"] = "Ne";
})(ConditionOperator || (ConditionOperator = {}));

const parseExpected = (actual, expected) => {
    if (Array.isArray(expected))
        return expected.filter((item) => item);
    if (typeof actual === 'boolean')
        return expected === 'true';
    if (typeof actual === 'number')
        return parseInt(expected, 10);
    if (actual instanceof Date)
        return new Date(expected);
    return expected;
};
const checkCondition = (actual, expected, operator) => {
    if ([null, undefined, NaN].includes(actual))
        return false;
    const parsedExpected = parseExpected(actual, expected);
    switch (operator) {
        case ConditionOperator.EQ:
            if (Array.isArray(actual) && Array.isArray(parsedExpected))
                return (actual.length === parsedExpected.length &&
                    actual.every((item, ind) => parsedExpected[ind] === item));
            return Array.isArray(parsedExpected)
                ? parsedExpected.includes((actual === null || actual === void 0 ? void 0 : actual.value) || actual)
                : parsedExpected === actual;
        case ConditionOperator.NE:
            if (Array.isArray(actual) && Array.isArray(parsedExpected))
                return !actual.some((item) => parsedExpected.includes(item));
            return Array.isArray(parsedExpected)
                ? !parsedExpected.includes((actual === null || actual === void 0 ? void 0 : actual.value) || actual)
                : parsedExpected !== actual;
        case ConditionOperator.GT:
            return actual > parsedExpected;
        case ConditionOperator.GE:
            return actual >= parsedExpected;
        case ConditionOperator.LT:
            return actual < parsedExpected;
        case ConditionOperator.LE:
            return actual <= parsedExpected;
        default:
            return null;
    }
};
const useConditionsWatch = (watch, conditions) => {
    const [shouldRender, setShouldRender] = useState(true);
    const cond = useMemo(() => conditions || [], [conditions]);
    const watched = cond.map(({ fieldAlias }) => watch(fieldAlias));
    useEffect(() => {
        const fullfilled = cond.every(({ value, operator }, index) => checkCondition(watched[index], value, operator));
        setShouldRender(fullfilled);
    }, [cond, watched]);
    if (!cond.length)
        return true;
    return shouldRender;
};

const utmParameters = [
    'utm_id',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
];

const parseRegex = (regex) => {
    if (!regex) {
        return undefined;
    }
    const parts = /\/(.*)\/(.*)/.exec(`${regex}`) || /\/(.*)\/(.*)/.exec(`/${regex}/`);
    return parts ? new RegExp(parts[1], parts[2]) : undefined;
};
const getQueries = (description) => {
    var _a;
    return description
        ? (_a = Object.values(JSON.parse(description).entityMap)) === null || _a === void 0 ? void 0 : _a.filter((item) => item.type === 'LEGAL-DOCUMENT')
        : [];
};
const getUtmParameters = () => {
    if (!isBrowser()) {
        return undefined;
    }
    return utmParameters.map((utm) => {
        const searchParams = new URLSearchParams(window.location.search);
        const paramValue = new URLSearchParams(searchParams).get(utm);
        return {
            name: utm,
            value: paramValue || undefined,
        };
    });
};

export { ConditionOperator, getQueries, getUtmParameters, parseRegex, useConditionsWatch };
